import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Alert, AlertDescription, Button, View, Text, DropdownField, InputField, NavLink } from "../../components/base";
import { Icon } from "../../components/base/Icon";
import { evaluatorActions, Evaluator, EvaluatorType, TargetFormat } from "../../redux/reducers/evaluatorSlice";
import { useAppDispatch, useAppSelector } from "../../redux";
import { useEffectiveOrganization } from "../../hooks/useEffectiveOrganization";
import { JsonSchemaBuilder } from "../../components/evaluations/JsonSchemaBuilder";
import { SuccessCriteriaBuilder } from "../../components/evaluations/SuccessCriteriaBuilder";
import { useEvaluatorSupportedModels } from "../../hooks/useEvaluatorSupportedModels";
import { organizationSecretsActions } from "../../redux/reducers/organizationSecretsSlice";

const defaultFormState = {
  title: "",
  evalType: EvaluatorType.EXTRACTION,
  targetFormat: TargetFormat.TRANSCRIPT,
  apiProvider: "",
  model: "",
  systemPrompt: "",
  responseSchema: "",
  successCriteria: "",
  code: "",
  organizationSecretName: "",
};

type EvaluatorFormValues = typeof defaultFormState;

const evaluatorTypeOptions = [
  { value: EvaluatorType.EXTRACTION, label: "Extraction" },
  { value: EvaluatorType.JUDGE, label: "Judge" },
  // Scripts coming soon
  // { value: EvaluatorType.SCRIPT, label: "Script" },
];

const targetFormatOptions = [
  { value: TargetFormat.TRANSCRIPT, label: "Transcript" },
  { value: TargetFormat.EVENTS, label: "Events" },
];

const parseJsonField = (value: string, fieldLabel: string) => {
  if (!value.trim()) {
    return undefined;
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    throw new Error(`${fieldLabel} must be valid JSON`);
  }
};

export const NewEvaluatorPage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { organization } = useEffectiveOrganization();
  const evaluatorsIsSaving = useAppSelector((state) => state.evaluator.isLoadingCreate);
  const { loading: supportedModelsLoading, getApiProviders, getModelsForProvider } = useEvaluatorSupportedModels();

  const organizationSecrets = useAppSelector((state) => Object.values(state.organizationSecrets.entities));

  const [formError, setFormError] = useState<string | null>(null);
  const [evaluatorForm, setEvaluatorForm] = useState<EvaluatorFormValues>(defaultFormState);

  const handleEvaluatorFormChange =
    <K extends keyof EvaluatorFormValues>(key: K) =>
    (value: EvaluatorFormValues[K]) => {
      setEvaluatorForm((prev) => ({ ...prev, [key]: value }));
    };

  const handleApiProviderChange = (value: string) => {
    const nextModels = getModelsForProvider("text", value);
    setEvaluatorForm((prev) => ({
      ...prev,
      apiProvider: value,
      model: nextModels.includes(prev.model) ? prev.model : nextModels[0] || "",
    }));
  };

  const handleCancel = () => {
    navigate("/evaluators");
  };

  const responseSchemaObject = useMemo(() => {
    if (!evaluatorForm.responseSchema.trim()) {
      return null;
    }

    try {
      return JSON.parse(evaluatorForm.responseSchema);
    } catch (error) {
      return null;
    }
  }, [evaluatorForm.responseSchema]);

  const successCriteriaObject = useMemo(() => {
    if (!evaluatorForm.successCriteria.trim()) {
      return null;
    }

    try {
      return JSON.parse(evaluatorForm.successCriteria);
    } catch (error) {
      return null;
    }
  }, [evaluatorForm.successCriteria]);

  const apiProviderOptions = useMemo(() => {
    return getApiProviders().map((provider) => ({
      value: provider,
      label: provider,
    }));
  }, [getApiProviders]);

  const modelOptions = useMemo(() => {
    return getModelsForProvider("text", evaluatorForm.apiProvider).map((model) => ({
      value: model,
      label: model,
    }));
  }, [evaluatorForm.apiProvider, getModelsForProvider]);

  const apiKeyOptions = useMemo(() => {
    return [
      { value: "", label: "VoiceRun default" },
      ...organizationSecrets.map((secret) => ({
        value: secret.name,
        label: secret.name,
      })),
    ];
  }, [organizationSecrets]);

  useEffect(() => {
    dispatch(organizationSecretsActions.get({}));
  }, [dispatch]);

  useEffect(() => {
    if (evaluatorForm.evalType === EvaluatorType.SCRIPT) {
      return;
    }

    if (!evaluatorForm.apiProvider && apiProviderOptions.length > 0) {
      const nextProvider = apiProviderOptions[0].value;
      const nextModels = getModelsForProvider("text", nextProvider);
      setEvaluatorForm((prev) => ({
        ...prev,
        apiProvider: nextProvider,
        model: nextModels[0] || "",
      }));
    }
  }, [apiProviderOptions, evaluatorForm.apiProvider, evaluatorForm.evalType, getModelsForProvider]);

  useEffect(() => {
    if (evaluatorForm.evalType === EvaluatorType.SCRIPT) {
      return;
    }

    if (!evaluatorForm.apiProvider || modelOptions.length === 0) {
      return;
    }

    const hasModel = modelOptions.some((option) => option.value === evaluatorForm.model);
    if (!hasModel) {
      setEvaluatorForm((prev) => ({
        ...prev,
        model: modelOptions[0]?.value || "",
      }));
    }
  }, [evaluatorForm.apiProvider, evaluatorForm.evalType, evaluatorForm.model, modelOptions]);

  const handleSubmit = async () => {
    if (!organization || evaluatorsIsSaving) {
      return;
    }

    try {
      const title = evaluatorForm.title.trim();
      if (!title) {
        throw new Error("Title is required.");
      }

      const payload: Partial<Evaluator> = {
        title,
        evalType: evaluatorForm.evalType,
        targetFormat: evaluatorForm.targetFormat,
      };

      if (evaluatorForm.evalType === EvaluatorType.SCRIPT) {
        if (!evaluatorForm.code.trim()) {
          throw new Error("Script code is required.");
        }
        payload.code = evaluatorForm.code;
        payload.systemPrompt = undefined;
        payload.responseSchema = undefined;
        payload.successCriteria = undefined;
        payload.apiProvider = undefined;
        payload.model = undefined;
      } else {
        if (!evaluatorForm.apiProvider) {
          throw new Error("Provider is required.");
        }
        if (!evaluatorForm.model) {
          throw new Error("Model is required.");
        }

        payload.apiProvider = evaluatorForm.apiProvider;
        payload.model = evaluatorForm.model;

        const systemPrompt = evaluatorForm.systemPrompt.trim();
        if (!systemPrompt) {
          throw new Error("System prompt is required.");
        }
        payload.systemPrompt = systemPrompt;

        const responseSchema = parseJsonField(evaluatorForm.responseSchema, "Response schema");
        if (evaluatorForm.evalType === EvaluatorType.JUDGE && !responseSchema) {
          throw new Error("Response schema is required for judge evaluators.");
        }
        payload.responseSchema = responseSchema;

        if (evaluatorForm.evalType === EvaluatorType.JUDGE) {
          const successCriteria = parseJsonField(evaluatorForm.successCriteria, "Success criteria");
          if (!successCriteria) {
            throw new Error("Success criteria is required for judge evaluators.");
          }
          payload.successCriteria = successCriteria;
        } else {
          payload.successCriteria = undefined;
        }

        payload.organizationSecretName = evaluatorForm.organizationSecretName || undefined;
      }

      setFormError(null);
      await dispatch(evaluatorActions.create(payload)).unwrap();
      navigate("/evaluators");
    } catch (error) {
      setFormError(error instanceof Error ? error.message : "Unable to create evaluator");
    }
  };

  return (
    <View className="flex flex-col flex-1 w-full min-h-full gap-6 p-6 bg-white dark:bg-slate-900 overflow-y-auto">
      <NavLink
        href="/evaluators"
        className="flex items-center w-fit p-0"
      >
        <Icon
          name="arrow-back"
          size={18}
          className="mr-1"
        />
        Evaluators
      </NavLink>

      <View className="flex flex-col gap-6 w-full">
        <View className="flex flex-col gap-2">
          <Text className="text-2xl font-semibold text-gray-900 dark:text-white">Create Evaluator</Text>
          <Text className="text-sm text-gray-600 dark:text-gray-400">Define how your organization grades or extracts insights from agent sessions.</Text>
        </View>

        {formError ? (
          <Alert variant="destructive">
            <AlertDescription>{formError}</AlertDescription>
          </Alert>
        ) : null}

        <View className="flex flex-col gap-4 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-6 shadow-sm w-full">
          <InputField
            label="Title"
            placeholder="Give this evaluator a descriptive name"
            value={evaluatorForm.title}
            onChange={handleEvaluatorFormChange("title")}
          />
          <DropdownField
            label="Evaluator Type"
            options={evaluatorTypeOptions}
            value={evaluatorForm.evalType}
            onChange={(value) => handleEvaluatorFormChange("evalType")(value as EvaluatorType)}
          />
          <DropdownField
            label="Target Format"
            options={targetFormatOptions}
            value={evaluatorForm.targetFormat}
            onChange={(value) => handleEvaluatorFormChange("targetFormat")(value as TargetFormat)}
            placeholder="Select target format"
          />
          {evaluatorForm.evalType !== EvaluatorType.SCRIPT ? (
            <View className="flex flex-col gap-4">
              <DropdownField
                label="Provider"
                options={apiProviderOptions}
                value={evaluatorForm.apiProvider}
                onChange={handleApiProviderChange}
                placeholder={supportedModelsLoading ? "Loading providers..." : "Select provider"}
                readOnly={supportedModelsLoading}
              />
              <DropdownField
                label="Model"
                options={modelOptions}
                value={evaluatorForm.model}
                onChange={(value) => handleEvaluatorFormChange("model")(value)}
                placeholder={supportedModelsLoading ? "Loading models..." : "Select model"}
                readOnly={supportedModelsLoading || !evaluatorForm.apiProvider}
              />
              <DropdownField
                label="API Key"
                options={apiKeyOptions}
                value={evaluatorForm.organizationSecretName}
                onChange={(value) => handleEvaluatorFormChange("organizationSecretName")(value)}
                placeholder="VoiceRun default"
                tooltip="Select an organization API key to use for this evaluator. Leave blank to use VoiceRun-managed keys."
              />
            </View>
          ) : null}
          {evaluatorForm.evalType !== EvaluatorType.SCRIPT ? (
            <InputField
              label="System Prompt"
              placeholder="Describe how the evaluator should behave"
              value={evaluatorForm.systemPrompt}
              onChange={handleEvaluatorFormChange("systemPrompt")}
              type="textarea"
              className="min-h-[128px]"
            />
          ) : null}
          {evaluatorForm.evalType === EvaluatorType.SCRIPT ? (
            <InputField
              label="Script"
              placeholder="Write a script to evaluate the session"
              value={evaluatorForm.code}
              onChange={handleEvaluatorFormChange("code")}
              type="textarea"
              className="min-h-[160px] font-mono"
            />
          ) : (
            <View className="flex flex-col gap-2">
              <Text className="text-sm font-medium text-gray-700 dark:text-gray-300">Response Schema</Text>
              <JsonSchemaBuilder
                value={responseSchemaObject as Record<string, unknown> | null}
                onChange={(schema) => handleEvaluatorFormChange("responseSchema")(schema ? JSON.stringify(schema, null, 2) : "")}
              />
            </View>
          )}
          {evaluatorForm.evalType === EvaluatorType.JUDGE ? (
            <View className="flex flex-col gap-2">
              <Text className="text-sm font-medium text-gray-700 dark:text-gray-300">Success Criteria</Text>
              <SuccessCriteriaBuilder
                schema={responseSchemaObject as Record<string, unknown> | null}
                value={successCriteriaObject as Record<string, unknown> | null}
                onChange={(criteria) => handleEvaluatorFormChange("successCriteria")(criteria ? JSON.stringify(criteria, null, 2) : "")}
              />
            </View>
          ) : null}
        </View>

        <View className="flex flex-row gap-3 justify-end">
          <Button
            variant="neutral"
            size="sm"
            outline
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            variant="success"
            size="sm"
            outline
            onClick={handleSubmit}
            disabled={evaluatorsIsSaving}
          >
            {evaluatorsIsSaving ? (
              <Icon
                name="loader"
                size={14}
                className="animate-spin"
              />
            ) : (
              <Icon
                name="add"
                size={14}
              />
            )}
            Create Evaluator
          </Button>
        </View>
      </View>
    </View>
  );
};
